import {
  ChangeEvent,
  ClipboardEvent,
  KeyboardEvent,
  useCallback,
  useMemo,
} from 'react';

import { sanitizeNationalNumber } from '../../logic/sanitizeNationalNumber';
import { stripCountryCallingCode } from '../../logic/stripCountryCallingCode';
import { PhoneInputProps } from '../../types/PhoneInputProps';
import { useParsedPhoneNumber } from '../useParsedPhoneNumber';
import { constructPhoneNumberFromCallingCodeAndNationalNumber } from '../usePhone/logic/constructPhoneNumberFromCallingCodeAndNationalNumber';
import { formatPhoneNumber } from './logic/formatPhoneNumber';
import { getPhoneNumberFormat } from './logic/getPhoneNumberFormat';

export const usePhone = (
  value: PhoneInputProps['value'],
  hooks: PhoneInputProps['hooks'],
  onChange: PhoneInputProps['onChange'],
  name: PhoneInputProps['name'],
) => {
  const { countryCallingCode, nationalNumber } = useParsedPhoneNumber(value);

  const onValueChange = useCallback(
    (newNumber: string) => {
      onChange(newNumber);

      hooks?.afterOnChange?.({
        name,
        value: newNumber,
      });
    },
    [hooks, onChange, name],
  );

  const onCountryChange = useCallback(
    (newCountryNumber: string) => {
      onValueChange(
        constructPhoneNumberFromCallingCodeAndNationalNumber(
          newCountryNumber,
          nationalNumber,
        ),
      );
    },
    [onValueChange, nationalNumber],
  );

  const onNumberChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>): void => {
      const newNationalNumber = sanitizeNationalNumber(event.target.value);

      onValueChange(
        constructPhoneNumberFromCallingCodeAndNationalNumber(
          countryCallingCode,
          newNationalNumber,
        ),
      );
    },
    [countryCallingCode, onValueChange],
  );

  // prevents leading zero input
  const onNumberKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>): void => {
      if (event.key !== '0') {
        return;
      }

      const { selectionStart } = event.currentTarget;
      if (selectionStart === null || selectionStart === undefined) {
        return;
      }

      const digitsBeforeCaret = sanitizeNationalNumber(
        event.currentTarget.value.slice(0, selectionStart),
      );

      if (digitsBeforeCaret.length === 0) {
        event.preventDefault();
      }
    },
    [],
  );

  // handle pasting phone numbers to remove leading zeros and non-digit characters
  const onNumberPaste = useCallback(
    (event: ClipboardEvent<HTMLInputElement>): void => {
      const pastedDigits = event.clipboardData
        .getData('text')
        .replace(/\D/g, '');
      if (!pastedDigits) {
        return;
      }

      event.preventDefault();
      const sanitizedFromPaste = sanitizeNationalNumber(
        stripCountryCallingCode(pastedDigits, countryCallingCode),
      );

      onValueChange(
        constructPhoneNumberFromCallingCodeAndNationalNumber(
          countryCallingCode,
          sanitizedFromPaste,
        ),
      );
    },
    [countryCallingCode, onValueChange],
  );

  const formatString = useMemo(
    () => getPhoneNumberFormat(nationalNumber),
    [nationalNumber],
  );

  const format = useCallback(
    (rawValue: string) => formatPhoneNumber(rawValue, formatString),
    [formatString],
  );

  return {
    countryCallingCode,
    onCountryChange,
    onNumberChange,
    onNumberKeyDown,
    onNumberPaste,
    nationalNumber,
    format,
  };
};
